import styled from "@emotion/styled";
import { ThemeMode } from "@smartsheet/sds-theme-internal";
import { createFocusRingStyles } from "../../../util/createFocusRingStyles";
import { chipCustomCssProperties } from "./chipConstants";
import { chipTheme } from "./chipTheme";

interface ChipContainerProps {
    isReadonly: boolean;
    size: "xsmall" | "small" | "medium";
    themeMode: ThemeMode;
}

export const ChipContainer = styled("div")<ChipContainerProps>(
    ({ size, themeMode, isReadonly }) => `
    box-sizing: border-box;
    height: var(--sds--chip--height, var(--sds--chip--height--${size}, ${chipTheme.height[size](themeMode)}));
    padding-left: var(--sds--chip--padding-left, var(--sds--chip--padding-x--${size}, 4px));
    padding-right: var(--sds--chip--padding-right, var(--sds--chip--padding-x--${size}, 4px));

    border-radius: var(--sds--chip--border-radius, var(--sds--chip--border-radius--${size}, ${chipTheme.borderRadius(
        themeMode
    )}));

    display: inline-flex;
    flex-direction: row;
    align-items: center;
    cursor: default;

    // For the X button's absolute position.
    position: relative;

    max-width: 100%;
    min-height: var(--sds--chip--min-height);
    transition: ${chipTheme.motion.animation(themeMode)};

    // TODO if we extract these 4 token usages behind 4 css properties private to the Chip, we can
    // set those in the component's render and not have to create a unique className for each of
    // these variants.
    color: var(${chipCustomCssProperties.text});
    background-color: var(${chipCustomCssProperties.backgroundRest});

    ${
        !isReadonly
            ? `
                    ${chipCustomCssProperties.removeButtonOpacity}: 0.0;
                    &:hover, &:focus {
                        background-color: var(${chipCustomCssProperties.backgroundHover});
                        ${chipCustomCssProperties.removeButtonOpacity}: 1.0;
                    }
        `
            : ""
    } // returning an empty string prevents the linter from complaining about null/undefined in a template literal

    
    ${createFocusRingStyles({
        theme: themeMode,
        // Place the ring just inside the content box.
        gap: -2,
        componentBorderWidth: 0,
        componentRadius: chipTheme.borderRadius(themeMode),
        focusSelector: ":focus-visible",
        componentRadiusOverride: `var(--sds--chip--border-radius, ${chipTheme.borderRadius(themeMode)})`,
    })}

    &:active {
        ${!isReadonly ? `background-color: var(${chipCustomCssProperties.backgroundActive});` : ""}
    }
`
);
